import { addDays, getISOWeek, getISOWeekYear } from 'date-fns'
import { AdherenceReportData } from './AdherenceReport.types'
import { dateToInt, intToDate } from './AdherenceReport.dateUtils'

export type WeeklyAdherenceAggregate = {
  dateKey: number
  isoYear: number
  isoWeek: number
  label: string
  unconstrainedStockTarget: number
  constrainedStockTarget: number
  weekStartInventory: number
  adherenceUnconstrainedPct: number
  adherenceConstrainedPct: number
}

type WeekTotals = {
  isoYear: number
  isoWeek: number
  rowCount: number
  unconstrainedStockTarget: number
  constrainedStockTarget: number
  weekStartInventory: number
}

type WeekSkeletonEntry = {
  dateKey: number
  isoYear: number
  isoWeek: number
  label: string
}

const labelFor = (isoYear: number, isoWeek: number): string =>
  `Wk ${String(isoWeek).padStart(2, '0')} ${isoYear}`

const calculateAdherencePct = (actual: number, target: number): number => {
  if (target === 0) {
    return 0
  }
  return ((actual - target) / target) * 100
}

// One entry per calendar week between startDate and endDate (inclusive),
// regardless of whether any adherence data exists for that week — this is
// what lets the chart show an x-axis tick for every week in the selected
// range instead of only the weeks that happen to have rows.
export const buildWeeklySkeleton = (
  startDate: number,
  endDate: number
): WeekSkeletonEntry[] => {
  const weeks: WeekSkeletonEntry[] = []
  let current = intToDate(startDate)
  const end = intToDate(endDate)

  while (current <= end) {
    const isoYear = getISOWeekYear(current)
    const isoWeek = getISOWeek(current)
    weeks.push({
      dateKey: dateToInt(current),
      isoYear,
      isoWeek,
      label: labelFor(isoYear, isoWeek),
    })
    current = addDays(current, 7)
  }

  return weeks
}

// Aggregates adherence rows by dateKey (one snapshot per week) across
// whatever site/sku/work-centre scope is currently selected. When a
// weekSkeleton is supplied, every week in it gets an entry — weeks with no
// matching data fall back to NaN so the chart still renders a tick/label for
// that week but leaves a gap instead of a misleading zero.
export const aggregateAdherenceByWeek = (
  data: AdherenceReportData[],
  weekSkeleton?: WeekSkeletonEntry[]
): WeeklyAdherenceAggregate[] => {
  const totalsByWeek = new Map<number, WeekTotals>()

  data.forEach((row) => {
    const existing = totalsByWeek.get(row.dateKey)
    if (existing) {
      existing.rowCount += 1
      existing.unconstrainedStockTarget += row.unconstrainedStockTarget
      existing.constrainedStockTarget += row.constrainedStockTarget
      existing.weekStartInventory += row.weekStartInventory
    } else {
      totalsByWeek.set(row.dateKey, {
        isoYear: row.isoYear,
        isoWeek: row.isoWeek,
        rowCount: 1,
        unconstrainedStockTarget: row.unconstrainedStockTarget,
        constrainedStockTarget: row.constrainedStockTarget,
        weekStartInventory: row.weekStartInventory,
      })
    }
  })

  const weeksToRender: WeekSkeletonEntry[] =
    weekSkeleton ??
    [...totalsByWeek.entries()]
      .sort(([dateKeyA], [dateKeyB]) => dateKeyA - dateKeyB)
      .map(([dateKey, totals]) => ({
        dateKey,
        isoYear: totals.isoYear,
        isoWeek: totals.isoWeek,
        label: labelFor(totals.isoYear, totals.isoWeek),
      }))

  return weeksToRender.map((week) => {
    const totals = totalsByWeek.get(week.dateKey)

    if (!totals) {
      return {
        dateKey: week.dateKey,
        isoYear: week.isoYear,
        isoWeek: week.isoWeek,
        label: week.label,
        unconstrainedStockTarget: Number.NaN,
        constrainedStockTarget: Number.NaN,
        weekStartInventory: Number.NaN,
        adherenceUnconstrainedPct: Number.NaN,
        adherenceConstrainedPct: Number.NaN,
      }
    }

    // Adherence % mirrors Adherence.table.tsx's groupedConstrainedAdherence /
    // groupedUnconstrainedAdherence aggregation functions: sum the raw actual
    // and target volumes across every row in scope, then take one ratio —
    // not an average of each row's own percentage. The displayed volume
    // figures use the average across rows, matching the table's own 'avg'
    // aggregation for those same columns; averaging vs summing here doesn't
    // change the ratio (both totals scale by the same row count).
    return {
      dateKey: week.dateKey,
      isoYear: week.isoYear,
      isoWeek: week.isoWeek,
      label: week.label,
      unconstrainedStockTarget:
        totals.unconstrainedStockTarget / totals.rowCount,
      constrainedStockTarget: totals.constrainedStockTarget / totals.rowCount,
      weekStartInventory: totals.weekStartInventory / totals.rowCount,
      adherenceUnconstrainedPct: calculateAdherencePct(
        totals.weekStartInventory,
        totals.unconstrainedStockTarget
      ),
      adherenceConstrainedPct: calculateAdherencePct(
        totals.weekStartInventory,
        totals.constrainedStockTarget
      ),
    }
  })
}
